import { Injectable } from '@angular/core';
import { getMessaging, getToken, onMessage, Messaging, isSupported } from 'firebase/messaging';
import { environment } from '../../environments/environment';
import { Subject } from 'rxjs';
import { initializeApp, getApps, getApp } from 'firebase/app';

export interface FcmPayload {
  title: string;
  body: string;
  icon?: string;
  data?: any;
}

@Injectable({ providedIn: 'root' })
export class FirebaseNotificationService {
  private messageSubject = new Subject<FcmPayload>();
  message$ = this.messageSubject.asObservable();
  private messaging: Messaging | null = null;
  // Promise kiểm tra trình duyệt/webview hiện tại có hỗ trợ Firebase Messaging hay không
  // (Push API, Notification API, Service Worker...). Nhiều in-app browser như Zalo, Telegram,
  // Messenger (đặc biệt trên iOS) KHÔNG hỗ trợ, gọi getMessaging() trực tiếp sẽ throw lỗi
  // đồng bộ ngay tại constructor và làm app trắng màn hình.
  private readonly supportPromise: Promise<boolean>;

  constructor() {
    this.supportPromise = this.initMessaging();
  }

  private async initMessaging(): Promise<boolean> {
    try {
      const supported = await isSupported();
      if (!supported) {
        console.warn('[FCM] Trình duyệt/app hiện tại không hỗ trợ Firebase Messaging, bỏ qua khởi tạo.');
        return false;
      }
      const app = getApps().length > 0 ? getApp() : initializeApp(environment.firebase);
      this.messaging = getMessaging(app);
      return true;
    } catch (err) {
      console.error('[FCM] Không thể khởi tạo Firebase Messaging:', err);
      this.messaging = null;
      return false;
    }
  }

  async requestPermissionAndGetToken(): Promise<string | null> {
    try {
      const supported = await this.supportPromise;
      if (!supported || !this.messaging || typeof Notification === 'undefined') {
        return null;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        console.warn('[FCM] Người dùng từ chối nhận thông báo');
        return null;
      }
      const token = await getToken(this.messaging, {
        vapidKey: environment.firebase.vapidKey
      });
      console.log('[FCM] Token:', token);
      return token;
    } catch (err) {
      console.error('[FCM] Lỗi lấy token:', err);
      return null;
    }
  }

  async listenForegroundMessages(): Promise<void> {
    try {
      const supported = await this.supportPromise;
      if (!supported || !this.messaging) {
        return;
      }
      onMessage(this.messaging, (payload) => {
        console.log('[FCM] Foreground message:', payload);
        this.messageSubject.next({
          title: payload.notification?.title ?? 'Thông báo',
          body: payload.notification?.body ?? '',
          icon: payload.notification?.icon,
          data: payload.data
        });
      });
    } catch (err) {
      console.error('[FCM] Lỗi lắng nghe message:', err);
    }
  }
}
